import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField,
} from '@mui/material';
import { CorporateTaxService } from '../../../services/corporateTax.service';
import { DeferredRequest, DeferredTax } from '../../../interfaces/corporateTax.interface';
import { useToast } from '../../../context/ToastContext';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { formatDate } from '../../../utils/dateUtils';
import ConfirmDialog from '../../common/ConfirmDialog';
import { DOC_STATUS_COLOR, currentIncomeYear, fiscalYearStartIso, incomeYearLabel, incomeYearOptions } from './corporateTaxText';

interface Props { institutionId: string; startMonth: number; canManage: boolean; canPost: boolean }

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <TableRow><TableCell>{label}</TableCell><TableCell align="right">{value}</TableCell></TableRow>
);

/** Simple deferred tax: (book NBV minus tax WDV) times the rate, posted as the movement of the net balance. */
const DeferredTab: React.FC<Props> = ({ institutionId, startMonth, canManage, canPost }) => {
    const toast = useToast();
    const [rows, setRows] = useState<DeferredTax[]>([]);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | undefined>();
    const [incomeYear, setIncomeYear] = useState(currentIncomeYear(startMonth) - 1);
    const [taxWdv, setTaxWdv] = useState('');
    const [ratePct, setRatePct] = useState('');
    const [notes, setNotes] = useState('');
    const [result, setResult] = useState<DeferredTax | null>(null);
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState<{ kind: 'post' | 'reverse' | 'delete'; row: DeferredTax } | null>(null);

    const load = useCallback(async () => {
        try { setRows(await CorporateTaxService.listDeferred(institutionId)); }
        catch (e) { toast.showApiError(e, 'Failed to load deferred tax'); }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const request = (): DeferredRequest => ({
        fiscalYearStart: fiscalYearStartIso(incomeYear, startMonth),
        taxWdv: Number(taxWdv || 0),
        ratePct: ratePct === '' ? null : Number(ratePct),
        notes: notes || null,
    });

    const openNew = () => {
        setEditingId(undefined); setTaxWdv(''); setRatePct(''); setNotes(''); setResult(null); setOpen(true);
    };
    const openExisting = (r: DeferredTax) => {
        setEditingId(r.id ?? undefined); setIncomeYear(Number(r.fiscalYearStart.substring(0, 4)));
        setTaxWdv(String(r.taxWdv)); setRatePct(String(r.ratePct)); setNotes(r.notes ?? ''); setResult(r); setOpen(true);
    };

    const preview = async () => {
        setBusy(true);
        try { setResult(await CorporateTaxService.previewDeferred(institutionId, request())); }
        catch (e) { toast.showApiError(e, 'Failed to compute deferred tax'); }
        finally { setBusy(false); }
    };
    const save = async () => {
        setBusy(true);
        try {
            const saved = await CorporateTaxService.saveDeferred(institutionId, request(), editingId);
            toast.showSuccess('Deferred tax saved as a draft');
            setEditingId(saved.id ?? undefined); setResult(saved); await load();
        } catch (e) { toast.showApiError(e, 'Failed to save deferred tax'); }
        finally { setBusy(false); }
    };
    const runConfirmed = async () => {
        if (!confirm || !confirm.row.id) return;
        setBusy(true);
        try {
            if (confirm.kind === 'post') await CorporateTaxService.postDeferred(institutionId, confirm.row.id);
            if (confirm.kind === 'reverse') await CorporateTaxService.reverseDeferred(institutionId, confirm.row.id);
            if (confirm.kind === 'delete') await CorporateTaxService.deleteDeferred(institutionId, confirm.row.id);
            toast.showSuccess('Done');
            setConfirm(null); setOpen(false); await load();
        } catch (e) { toast.showApiError(e, 'The action failed'); }
        finally { setBusy(false); }
    };

    const isDraft = result?.status === 'DRAFT';
    const editable = !result || !result.id || isDraft;
    const side = (n: number) => (n >= 0 ? 'liability' : 'asset');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>{canManage && <Button data-help="accounting.corpTax.def.new" variant="contained" onClick={openNew}>New deferred tax run</Button>}</Box>
            <TableContainer component={Paper}>
                <Table data-help-section="accounting.corpTax.def.table" size="small" aria-label="Deferred tax runs">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.corpTax.def.col.year">Fiscal year</TableCell><TableCell data-help="accounting.corpTax.def.col.difference" align="right">Temporary difference</TableCell>
                            <TableCell data-help="accounting.corpTax.def.col.closing" align="right">Closing balance</TableCell><TableCell data-help="accounting.corpTax.def.col.movement" align="right">Movement</TableCell>
                            <TableCell data-help="accounting.corpTax.def.col.status">Status</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => (
                            <TableRow key={r.id ?? r.fiscalYearStart} hover>
                                <TableCell>{formatDate(r.fiscalYearStart)} to {formatDate(r.fiscalYearEnd)}</TableCell>
                                <TableCell align="right">{formatMoney(r.temporaryDifference)}</TableCell>
                                <TableCell align="right">{formatMoney(Math.abs(r.closingBalance))} {side(r.closingBalance)}</TableCell>
                                <TableCell align="right">{formatMoney(r.movement)}</TableCell>
                                <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={DOC_STATUS_COLOR[r.status]} /></TableCell>
                                <TableCell><Button data-help="accounting.corpTax.def.action.open" size="small" onClick={() => openExisting(r)}>Open</Button></TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && <TableRow><TableCell colSpan={6}>No deferred tax runs yet.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog data-help-page="accounting.corpTax.def.form.page" open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Deferred tax</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        <TextField data-help="accounting.corpTax.def.form.year" select size="small" label="Income year" value={incomeYear} disabled={!!editingId || !editable}
                            sx={{ flex: '1 1 180px', maxWidth: 240 }} onChange={e => setIncomeYear(Number(e.target.value))}>
                            {incomeYearOptions(startMonth).map(y => <MenuItem key={y} value={y}>{incomeYearLabel(y, startMonth)}</MenuItem>)}
                        </TextField>
                        <TextField data-help="accounting.corpTax.def.form.wdv" size="small" label="Tax written-down value" type="number" value={taxWdv} disabled={!editable}
                            sx={{ flex: '1 1 200px', maxWidth: 260 }} onChange={e => setTaxWdv(e.target.value)}
                            helperText="Fixed assets' value under the tax depreciation schedule" />
                        <TextField data-help="accounting.corpTax.def.form.rate" size="small" label="Rate %" type="number" value={ratePct} disabled={!editable}
                            sx={{ flex: '1 1 120px', maxWidth: 160 }} onChange={e => setRatePct(e.target.value)}
                            helperText="Blank uses the loaded rate" />
                    </Box>
                    <TextField data-help="accounting.corpTax.def.form.notes" size="small" label="Notes" value={notes} multiline minRows={2} fullWidth disabled={!editable}
                        onChange={e => setNotes(e.target.value)} />
                    {result && (
                        <>
                            {result.warnings.map(w => <Alert key={w} severity="warning">{w}</Alert>)}
                            <TableContainer component={Paper} variant="outlined">
                                <Table data-help-section="accounting.corpTax.def.breakdown" size="small" aria-label="Deferred tax breakdown">
                                    <TableBody>
                                        <Row label="Book net value of fixed assets" value={formatMoney(result.bookNbv)} />
                                        <Row label="Tax written-down value" value={formatMoney(result.taxWdv)} />
                                        <Row label="Temporary difference" value={formatMoney(result.temporaryDifference)} />
                                        <Row label={`At ${result.ratePct}%, closing balance`} value={`${formatMoney(Math.abs(result.closingBalance))} ${side(result.closingBalance)}`} />
                                        <Row label="Opening balance" value={`${formatMoney(Math.abs(result.openingBalance))} ${side(result.openingBalance)}`} />
                                        <Row label="Movement to post" value={formatMoney(result.movement)} />
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.corpTax.def.form.close" onClick={() => setOpen(false)}>Close</Button>
                    {editable && canManage && <Button data-help="accounting.corpTax.def.preview" disabled={busy} onClick={preview}>Preview</Button>}
                    {editable && canManage && <Button data-help="accounting.corpTax.def.saveDraft" variant="outlined" disabled={busy} onClick={save}>Save draft</Button>}
                    {isDraft && result?.id && canManage && (
                        <Button data-help="accounting.corpTax.def.deleteDraft" color="error" disabled={busy} onClick={() => setConfirm({ kind: 'delete', row: result })}>Delete draft</Button>
                    )}
                    {isDraft && result?.id && canPost && (
                        <Button data-help="accounting.corpTax.def.post" variant="contained" disabled={busy} onClick={() => setConfirm({ kind: 'post', row: result })}>Post</Button>
                    )}
                    {result?.status === 'POSTED' && canPost && (
                        <Button data-help="accounting.corpTax.def.reverse" color="error" disabled={busy} onClick={() => setConfirm({ kind: 'reverse', row: result })}>Reverse</Button>
                    )}
                </DialogActions>
            </Dialog>

            <ConfirmDialog open={confirm !== null} submitting={busy}
                title={confirm?.kind === 'post' ? 'Post deferred tax?' : confirm?.kind === 'reverse' ? 'Reverse deferred tax?' : 'Delete this draft?'}
                message={confirm?.kind === 'post' ? 'Posts the movement of the deferred tax balance to the ledger.'
                    : confirm?.kind === 'reverse' ? 'Posts an exact reversing journal.' : 'The draft is removed.'}
                confirmLabel={confirm?.kind === 'post' ? 'Post' : confirm?.kind === 'reverse' ? 'Reverse' : 'Delete'}
                confirmColor={confirm?.kind === 'post' ? 'primary' : 'error'}
                onCancel={() => setConfirm(null)} onConfirm={runConfirmed} />
        </Box>
    );
};

export default DeferredTab;
